import { Injectable, EventEmitter } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import {
    Observable,
    BehaviorSubject,
    of,
    forkJoin,
    Subject,
    combineLatest,
    throwError,
    from,
} from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    switchMap,
    catchError,
    finalize,
    tap,
    map,
    filter,
    take,
    takeUntil,
    startWith,
    delay,
    shareReplay,
} from 'rxjs/operators';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import {
    ChatUser,
    FirestoreUser,
    Chat,
    ChatMessage,
    NewMessage,
    StaffNewChat,
    StaffChat,
    ChatWithStaffUser,
    UserStaffChat,
    StaffChatUser,
    ChatWithUser,
} from '../model/chat.model';
import { LookupService } from './lookup.service';
import { WebStorage } from '../components/auth/storage/web.storage';
import * as moment from 'moment-timezone';
import { Common } from '../utils/common';
import { MessageService } from 'primeng/api';
import { HttpResponse } from '@angular/common/http';
import { StaffDTO } from '../model/StaffDTO';
import { RoomDTO } from '../model/RoomDTO';
import { PhotoService } from './photo.service';

@Injectable({
    providedIn: 'root',
})
export class StaffChatService {
    private readonly _selectedChatSubject =
        new BehaviorSubject<ChatWithStaffUser | null>(null);
    public readonly selectedChat$ = this._selectedChatSubject.asObservable();

    private readonly _newMessageSubject = new Subject<
        ChatMessage & { chatId: string }
    >();
    public readonly newMessage$ = this._newMessageSubject.asObservable();

    private currentUserId: string = '';

    private sidebarRefreshSource = new Subject<void>();
    sidebarRefresh$ = this.sidebarRefreshSource.asObservable();

    // Event emitter for message deletion
    messageDeleted = new EventEmitter<{
        chatId: string;
        messageId: string;
        deletedBy: string;
    }>();

    constructor(
        private firestore: AngularFirestore,
        private storage: AngularFireStorage,
        private lookupService: LookupService,
        private webStorage: WebStorage,
        private messageService: MessageService,
        private photoService: PhotoService
    ) {
        this.currentUserId = this.webStorage.getUserData().refTableId;
    }

    triggerSidebarRefresh() {
        this.sidebarRefreshSource.next();
    }

    // Cached profile photo retrieval using PhotoService
    getPhotoUrlCached(photoUid: string): Observable<string> {
        return this.photoService.getPhotoUrl(photoUid);
    }

    // Create or update a user in Firestore
    async createOrUpdateUser(userData: {
        id: string | number;
        name: string;
        email: string;
        role: 'staff' | 'staff2';
        photoUrl?: string;
        schoolId: number;
        roomId?: number;
        roomIds?: number[];
        phoneNumber?: string;
        status?: string;
        profileStatus?: string;
        employeeId?: string;
    }): Promise<void> {
        if (!userData.id) {
            throw new Error('User ID is required');
        }

        if (!userData.name) {
            throw new Error('User name is required');
        }

        if (userData.schoolId === undefined || userData.schoolId === null) {
            throw new Error('School ID is required');
        }

        // Format the user ID based on role
        const formattedId =
            userData.role === 'staff'
                ? `staff_${userData.id}`
                : `staff2_${userData.id}`;

        const userProfile: any = {
            name: userData.name.trim(),
            email: userData.email || `${formattedId}@example.com`,
            photoURL: userData.photoUrl || 'assets/layout/images/male.png',
            lastSeen: Date.now(),
            status: userData.status || 'online',
            profileStatus: userData.profileStatus || userData.status || 'A',
            role: userData.role,
        };

        if (userData.role === 'staff' && userData.roomIds) {
            userProfile.roomIds = userData.roomIds;
        }

        try {
            const userRef = this.firestore.doc(`users/${formattedId}`);
            const userDoc = await userRef.get().toPromise();

            // If user exists, preserve the existing schoolId
            const existingData = userDoc?.data() as any;
            const finalSchoolId = existingData?.schoolId || userData.schoolId;

            await userRef.set(
                {
                    id: formattedId,
                    profile: userProfile,
                    schoolId: finalSchoolId,
                },
                { merge: true }
            );
        } catch (error) {
            console.error('Error creating/updating user:', error);
            throw error;
        }
    }

    /**
     * Find an existing chat between two staff members
     * Checks both directions (staff1-staff2 and staff2-staff1)
     * Returns chatId if found, null otherwise
     */
    public async findExistingStaffChat(
        staffId: string,
        staffId2: string
    ): Promise<string | null> {
        try {
            const currentUser = this.webStorage.getUserData();
            const staff1Id = `staff_${staffId}`;
            const staff2Id = `staff_${staffId2}`;

            // Base query conditions
            const baseQuery = (ref: any) => {
                let query = ref
                    .where(`participants.${staff1Id}`, '==', true)
                    .where(`participants.${staff2Id}`, '==', true);

                // Add school filter
                query = query.where('schoolId', '==', currentUser.schoolId);

                return query;
            };

            // First check in chats collection for both directions
            const chatQuerySnapshot1 = await this.firestore
                .collection<StaffChat>('staffChats', (ref) =>
                    baseQuery(ref).where('staff2Info.id', '==', staff2Id)
                )
                .get()
                .toPromise();

            const chatQuerySnapshot2 = await this.firestore
                .collection<StaffChat>('staffChats', (ref) =>
                    baseQuery(ref).where('staff2Info.id', '==', staff1Id)
                )
                .get()
                .toPromise();

            // Check first direction
            if (chatQuerySnapshot1 && !chatQuerySnapshot1.empty) {
                const chatId = chatQuerySnapshot1.docs[0].id;
                const userChatDoc = await this.firestore
                    .collection('staffUserChats')
                    .doc(`staff_${staffId}`)
                    .collection('staffChats')
                    .doc(chatId)
                    .get()
                    .toPromise();

                if (userChatDoc?.exists) {
                    return chatId;
                }
            }

            // Check second direction
            if (chatQuerySnapshot2 && !chatQuerySnapshot2.empty) {
                const chatId = chatQuerySnapshot2.docs[0].id;
                const userChatDoc = await this.firestore
                    .collection('staffUserChats')
                    .doc(`staff_${staffId2}`)
                    .collection('staffChats')
                    .doc(chatId)
                    .get()
                    .toPromise();

                if (userChatDoc?.exists) {
                    return chatId;
                }
            }

            // If not found in chats collection, check staffUserChats directly for both directions
            const userChatQuerySnapshot1 = await this.firestore
                .collection('staffUserChats')
                .doc(`staff_${staffId}`)
                .collection('staffChats', (ref) =>
                    ref.where('staff2Info.id', '==', `staff_${staffId2}`)
                )
                .get()
                .toPromise();

            const userChatQuerySnapshot2 = await this.firestore
                .collection('staffUserChats')
                .doc(`staff_${staffId2}`)
                .collection('staffChats', (ref) =>
                    ref.where('staff2Info.id', '==', `staff_${staffId}`)
                )
                .get()
                .toPromise();

            if (userChatQuerySnapshot1 && !userChatQuerySnapshot1.empty) {
                return userChatQuerySnapshot1.docs[0].id;
            }

            if (userChatQuerySnapshot2 && !userChatQuerySnapshot2.empty) {
                return userChatQuerySnapshot2.docs[0].id;
            }

            return null;
        } catch (error) {
            console.error('Error finding existing chat:', error);
            return null;
        }
    }

    async createStaffNewChat(newChat: StaffNewChat): Promise<string> {
        const currentUser = this.webStorage.getUserData();

        // First check if a staff chat already exists
        const existingChatId = await this.findExistingStaffChat(
            newChat.staffId,
            newChat.staffId2
        );
        if (existingChatId) {
            // Send message to existing chat
            await this.sendStaffMessage({
                chatId: existingChatId,
                text: newChat.initialMessage,
                senderId: `staff_${newChat.staffId}`,
                participants: Object.keys({
                    [`staff_${newChat.staffId}`]: true,
                }),
            });
            return existingChatId;
        }

        // If no existing chat, create a new one
        const chatId = `staffChat_${Date.now()}`;
        const messageId = Date.now().toString();
        const timestamp = Date.now();

        // Create participants object with all staff
        const chatData: Omit<StaffChat, 'chatId' | 'messages'> & {
            schoolId: number;
        } = {
            participants: {
                [`staff_${newChat.staffId}`]: true,
                [`staff_${newChat.staffId2}`]: true,
            },
            staffInfo: {
                id: `staff_${newChat.staffId}`,
                name: newChat.staffName,
            },
            staff2Info: {
                id: `staff_${newChat.staffId2}`,
                name: newChat.staffName2,
            },
            createdAt: Date.now(),
            lastMessage: {
                text: newChat.initialMessage,
                senderId: `staff_${newChat.staffId}`,
                timestamp: Date.now(),
                isDeleted: false,
            },
            schoolId: currentUser.schoolId || 0, // Will be validated above if not admin
        };

        // Create staff chat document
        const chat: StaffChat = {
            chatId: chatId,
            ...chatData,
            messages: [
                {
                    id: messageId,
                    text: newChat.initialMessage,
                    senderId: `staff_${newChat.staffId}`,
                    timestamp: timestamp,
                    type: 'text',
                    status: 'sent',
                    isDeleted: false,
                    readBy: {
                        [`staff_${newChat.staffId}`]: true,
                    },
                },
            ],
        };

        // Batch write
        const batch = this.firestore.firestore.batch();

        // Set staff chat document
        const chatRef = this.firestore.doc(`staffChats/${chatId}`).ref;
        batch.set(chatRef, chat);

        // Update staffUserChats for all participants
        const userChatData: UserStaffChat = {
            lastMessage: {
                text: newChat.initialMessage,
                senderId: `staff_${newChat.staffId}`,
                timestamp: timestamp,
                isDeleted: false,
            },
            unreadCount: 0,
            staffInfo: chat.staffInfo,
            staff2Info: chat.staff2Info,
            schoolId: chat.schoolId,
        };

        // Add staffUserChats for all participants
        Object.keys(chat.participants).forEach((participantId) => {
            const participantChatRef = this.firestore
                .collection('staffUserChats')
                .doc(participantId)
                .collection('staffChats')
                .doc(chatId).ref;
            batch.set(participantChatRef, userChatData);
        });

        try {
            await batch.commit();
            return chatId;
        } catch (error) {
            console.error('Error creating chat:', error);
            throw error;
        }
    }

    // Set selected chat
    setSelectedChat(chat: ChatWithStaffUser | null) {
        this._selectedChatSubject.next(chat);
    }

    // Get selected chat
    getSelectedChat(): Observable<ChatWithStaffUser | null> {
        return this.selectedChat$;
    }

    // Get messages for selected chat
    getSelectedChatMessages(): Observable<ChatMessage[]> {
        return this.selectedChat$.pipe(
            filter((chat) => !!chat),
            switchMap((chat) => {
                if (!chat) return of([]);
                return this.firestore
                    .collection<ChatMessage>(
                        `staffChats/${chat.chatId}/messages`,
                        (ref) => ref.orderBy('timestamp', 'asc')
                    )
                    .valueChanges();
            })
        );
    }

     /**
     * Get all chats for a user, fully reactive
     * @param userId The ID of the user to get chats for
     */
    getUserChats(userId: string): Observable<ChatWithStaffUser[]> {
        const currentUser = this.webStorage.getUserData();

        return this.firestore
            .collection<StaffChat>('staffChats', (ref) => {
                return ref
                    .where(`participants.${userId}`, '==', true)
                    .where('schoolId', '==', currentUser.schoolId);
            })
            .snapshotChanges()
            .pipe(
                switchMap((actions) => {
                    if (!actions || actions.length === 0) {
                        return of([]);
                    }

                    // Map each chat action to a stream of its full data
                    const chatObservables = actions.map((a) => {
                        const data = a.payload.doc.data() as StaffChat;
                        const chatId = a.payload.doc.id;

                        // 1. Identify other user
                        const participantKeys = Object.keys(data.participants || {});
                        const otherStaffId = participantKeys.find((id) => id !== userId);

                        if (!otherStaffId) return of(null);

                        // 2. Stream of Other User Profile (using take(1) to avoid infinite loops if it changes frequently)
                        // Or better, use a lighter valueChanges()
                        const otherUser$ = this.firestore
                            .doc(`users/${otherStaffId}`)
                            .valueChanges()
                            .pipe(
                                // tap(u => console.log(`👤 Profile for ${otherStaffId}:`, u)),
                                startWith(null)
                            );

                        // 3. Stream of StaffUserChat data (specific to this user)
                        const userChat$ = this.firestore
                            .collection('staffUserChats')
                            .doc(userId)
                            .collection<UserStaffChat>('staffChats')
                            .doc(chatId)
                            .valueChanges()
                            .pipe(startWith(null));

                        // Combine sources for this single chat
                        return combineLatest([
                            of({ ...data, chatId }),
                            userChat$,
                            otherUser$
                        ]).pipe(
                            map(([chatData, userChatData, otherUserData]) => {
                                const finalChatData = userChatData || {
                                    lastMessage: chatData.lastMessage,
                                    unreadCount: 0,
                                    staffInfo: chatData.staffInfo,
                                    staff2Info: chatData.staff2Info
                                };

                                // Create Other User object with better fallbacks
                                // Check if profile is nested or flat
                                const profile = (otherUserData as any)?.profile || otherUserData || {};
                                
                                // Determine the other user's name from chat data
                                // The otherStaffId is the participant who is NOT the current user
                                let otherUserName = 'Unknown Staff';
                                if (chatData.staffInfo?.id === otherStaffId) {
                                    otherUserName = chatData.staffInfo?.name || 'Unknown Staff';
                                } else if (chatData.staff2Info?.id === otherStaffId) {
                                    otherUserName = chatData.staff2Info?.name || 'Unknown Staff';
                                }
                                
                                const otherUser = {
                                    id: otherStaffId,
                                    name: profile?.name || profile?.displayName || otherUserName,
                                    email: profile?.email || '',
                                    role: 'staff' as const,
                                    photoUrl: profile?.photoURL || profile?.photoUrl || 'assets/layout/images/male.png',
                                    status: (profile?.status || 'A'),
                                    roomIds: profile?.roomIds || []
                                };
                                
                                return {
                                    chatId: chatData.chatId,
                                    lastMessage: finalChatData.lastMessage,
                                    unreadCount: finalChatData.unreadCount || 0,
                                    otherUser,
                                    type: 'staff' as const
                                } as ChatWithStaffUser;
                            })
                        );
                    });

                    // Combine all individual chat streams
                    return combineLatest(chatObservables).pipe(
                        map(results => results.filter(c => c !== null) as ChatWithStaffUser[])
                    );
                }),
                map(chats => {
                    return chats.sort((a, b) => {
                        const timeA = a.lastMessage?.timestamp || 0;
                        const timeB = b.lastMessage?.timestamp || 0;
                        return timeB - timeA;
                    });
                }),
                // Debounce emissions to prevent UI thrashing when multiple profiles update at once
                debounceTime(100),
                catchError((error) => {
                    console.error('Error in getUserChats:', error);
                    return of([]);
                })
            );
    }

    // Get all messages for a chat
    getChatMessages(chatId: string): Observable<ChatMessage[]> {
        const currentUser = this.webStorage.getUserData();

        return this.firestore
            .doc<StaffChat>(`staffChats/${chatId}`)
            .valueChanges()
            .pipe(
                switchMap((chat) => {
                    if (!chat) {
                        return throwError(() => new Error('Chat not found'));
                    }

                    // If user is not admin, verify school access

                    const messages = chat.messages || [];
                    return of(
                        messages.sort((a, b) => a.timestamp - b.timestamp)
                    );
                }),
                catchError((error) => {
                    console.error('Error getting chat messages:', error);
                    return of([]);
                })
            );
    }

    // Get chat details
    getChat(chatId: string): Observable<StaffChat | null> {
        const currentUser = this.webStorage.getUserData();

        return this.firestore
            .doc<StaffChat>(`staffChats/${chatId}`)
            .valueChanges()
            .pipe(
                map((chat) => {
                    if (!chat) {
                        return null;
                    }

                    // If user is not admin, verify school access

                    return chat;
                }),
                catchError((error) => {
                    console.error('Error getting chat:', error);
                    return of(null);
                })
            );
    }

    async sendStaffMessage(message: NewMessage): Promise<void> {
        const messageId = (message as any).id || Date.now().toString();
        const timestamp = Date.now();

        // Create message object
        const messageData: ChatMessage = {
            id: messageId,
            text: message.text,
            senderId: message.senderId,
            timestamp: timestamp,
            type: 'text',
            status: 'sent',
            isDeleted: false,
            readBy: {
                [message.senderId]: true,
            },
        };

        // Get chat document
        const chatRef = this.firestore
            .collection('staffChats')
            .doc(message.chatId);
        const chatDoc = await chatRef.get().toPromise();

        if (!chatDoc?.exists) {
            throw new Error('Chat not found');
        }

        const chat = chatDoc.data() as StaffChat;

        // Get all participants from the chat document
        const participants = Object.keys(chat.participants);

        // Batch write
        const batch = this.firestore.firestore.batch();

        // Update chat document with new message
        batch.update(chatRef.ref, {
            messages: [...(chat.messages || []), messageData],
            lastMessage: {
                text: message.text,
                senderId: message.senderId,
                timestamp: timestamp,
                isDeleted: false,
                status: 'sent',
            },
            updatedAt: timestamp,
        });

        // Update staffUserChats for all participants
        const userChatData = {
            lastMessage: {
                text: message.text,
                senderId: message.senderId,
                timestamp: timestamp,
                isDeleted: false,
                status: 'sent',
            },
            staffInfo: chat.staffInfo,
            staff2Info: chat.staff2Info,
            updatedAt: timestamp,
        };

        // Update unread counts for all participants except sender
        for (const participantId of participants) {
            const participantChatRef = this.firestore
                .collection('staffUserChats')
                .doc(participantId)
                .collection('staffChats')
                .doc(message.chatId).ref;

            if (participantId !== message.senderId) {
                // For non-senders, increment unread count and set last message
                batch.set(
                    participantChatRef,
                    {
                        ...userChatData,
                        unreadCount: firebase.firestore.FieldValue.increment(1),
                    },
                    { merge: true }
                );
            } else {
                // For sender, just update last message and other info (reset unread to 0)
                batch.set(
                    participantChatRef, 
                    {
                        ...userChatData,
                        unreadCount: 0
                    }, 
                    { merge: true }
                );
            }
        }

        // Emit to newMessage$ so sidebar can clear search
        this._newMessageSubject.next({
            ...messageData,
            chatId: message.chatId
        });

        try {
            await batch.commit();
        } catch (error) {
            console.error('Error sending message:', error);
            throw error;
        }
    }

    markChatAsRead(chatId: string, userId: string): Observable<void> {
        return this.getChatMessages(chatId).pipe(
            take(1),
            switchMap((messages) => {
                const unreadMessages = messages.filter((message) => {
                    if (message.senderId === userId) return false;
                    if (!message.readBy) return true;
                    return !message.readBy[userId];
                });

                if (unreadMessages.length === 0) {
                    return of(undefined);
                }

                const updates = unreadMessages.map((message) => {
                    const updatedReadBy = message.readBy
                        ? { ...message.readBy, [userId]: true }
                        : { [userId]: true };
                    return this.updateMessage(chatId, message.id, {
                        readBy: updatedReadBy,
                    });
                });

                return forkJoin(updates).pipe(map(() => undefined));
            })
        );
    }

    getParticipantNames(chatId: string): Observable<string[]> {
        return this.firestore
            .collection('staffChats')
            .doc(chatId)
            .get()
            .pipe(
                switchMap((chatDoc) => {
                    const chatData = chatDoc.data() as StaffChat;
                    if (!chatData || !chatData.participants) return of([]);

                    const participantIds = Object.keys(chatData.participants);
                    if (!participantIds.length) return of([]);

                    const userObservables = participantIds.map((id) =>
                        this.getUser(id)
                    );
                    return combineLatest(userObservables);
                }),
                map((users) =>
                    users
                        .filter((user): user is ChatUser => !!user) // Remove nulls
                        .sort((a, b) => {
                            // Staff first
                            if (a.role === 'staff' && b.role !== 'staff')
                                return -1;
                            if (a.role !== 'staff' && b.role === 'staff')
                                return 1;
                            return 0;
                        })
                        .map((user) => user.name)
                )
            );
    }

    // Get user details
    getUser(userId: string): Observable<ChatUser | null> {
        if (!userId) return of(null);

        return this.firestore
            .doc<FirestoreUser>(`users/${userId}`)
            .valueChanges()
            .pipe(
                map((user) => {
                    if (!user) return null;
                    return {
                        id: user.id,
                        name: user.profile.name,
                        email: user.profile.email,
                        role: user.profile.role,
                        photoUrl: user.profile.photoURL,
                        lastSeen: user.profile.lastSeen,
                        status: user.profile.status,
                    };
                })
            );
    }

    formatTimestampForSidebar(timestamp: number): string {
        const now = moment().tz(Common.timeZone);
        const messageDate = moment(timestamp).tz(Common.timeZone);

        if (now.isSame(messageDate, 'day')) {
            return messageDate.format('hh:mm A');
        } else if (now.clone().subtract(1, 'day').isSame(messageDate, 'day')) {
            return 'Yesterday';
        } else {
            return messageDate.format('DD/MM/YYYY');
        }
    }

    formatMessageDate(timestamp: number): string {
        const today = moment().tz(Common.timeZone);
        const messageDate = moment(timestamp).tz(Common.timeZone);

        if (today.isSame(messageDate, 'day')) {
            return 'Today';
        } else if (
            today.clone().subtract(1, 'day').isSame(messageDate, 'day')
        ) {
            return 'Yesterday';
        } else {
            return messageDate.format('DD MMM, YYYY');
        }
    }

    formatTime(timestamp: number): string {
        return moment(timestamp).tz(Common.timeZone).format('hh:mm A');
    }

    getMessageTime(timestamp: number): string {
        const messageDate = new Date(timestamp);
        return messageDate.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
        });
    }

    getMessages(chatId: string): Observable<ChatMessage[]> {
        return this.getChatMessages(chatId);
    }

    // Get all users in a chat
    getChatUsers(chatId: string): Observable<StaffChatUser[]> {
        return this.getChat(chatId).pipe(
            switchMap((chat) => {
                if (!chat || !chat.participants) {
                    return of([]);
                }

                const participantIds = Object.keys(chat.participants);

                const userObservables = participantIds.map((userId) =>
                    this.firestore
                        .doc<{
                            id: string;
                            profile: {
                                name: string;
                                email: string;
                                photoURL: string;
                                status: string;
                                role: string;
                            };
                        }>(`users/${userId}`)
                        .valueChanges()
                        .pipe(
                            take(1),
                            map((user) => {
                                if (!user || !user.profile) {
                                    return null;
                                }

                                const chatUser: StaffChatUser = {
                                    id: userId, // Keep full id like staff_1 or parent_1
                                    name: user.profile.name || 'Unknown User',
                                    email: user.profile.email || '',
                                    role: 'staff',
                                    photoUrl:
                                        user.profile.photoURL ||
                                        'assets/layout/images/male.png',
                                    lastSeen: Date.now(),
                                    status: user.profile.status as
                                        | 'online'
                                        | 'offline',
                                    employeeId: '',
                                };

                                return chatUser;
                            }),
                            catchError((error) => {
                                console.error(
                                    `Error fetching user ${userId}:`,
                                    error
                                );
                                return of(null);
                            }),
                            filter(
                                (user): user is StaffChatUser => user !== null
                            )
                        )
                );

                return forkJoin(userObservables).pipe(
                    map((users) =>
                        users.filter(
                            (user): user is StaffChatUser => user !== null
                        )
                    )
                    // tap(users => console.log('Final chat users:', users))
                );
            }),
            catchError((error) => {
                console.error('Error in getChatUsers:', error);
                return of([]);
            })
        );
    }

    // Add new method for soft deleting messages
    async deleteMessage(
        chatId: string,
        messageId: string,
        currentUserId: string
    ): Promise<void> {
        try {
            const chatDocRef = this.firestore.doc(`staffChats/${chatId}`);
            const chatSnap = await chatDocRef.get().toPromise();

            if (!chatSnap?.exists) {
                throw new Error('Chat not found');
            }

            const chat = chatSnap.data() as any;

            if (!chat.messages || !Array.isArray(chat.messages)) {
                throw new Error('No messages found in chat');
            }

            const messageIndex = chat.messages.findIndex(
                (m: any) => m.id === messageId
            );
            if (messageIndex === -1) {
                throw new Error('Message not found');
            }

            const message = chat.messages[messageIndex];

            if (message.senderId !== currentUserId) {
                throw new Error('You can only delete your own messages');
            }

            // Soft delete the message
            const updatedMessages = [...chat.messages];
            updatedMessages[messageIndex] = {
                ...message,
                isDeleted: true,
                deletedAt: new Date(),
            };

            // Update the messages array in the chat document
            await chatDocRef.update({ messages: updatedMessages });

            // Only update lastMessage if the deleted message is the last one
            const isLastMessage = messageIndex === chat.messages.length - 1;

            if (isLastMessage) {
                const lastMessageUpdate = {
                    senderId: message.senderId,
                    timestamp: message.timestamp,
                    isDeleted: true,
                };

                // Update lastMessage in chat doc
                await chatDocRef.update({ lastMessage: lastMessageUpdate });

                // Update staffUserChats for all participants
                const batch = this.firestore.firestore.batch();
                const participants = Object.keys(chat.participants || {});

                for (const participantId of participants) {
                    const userChatRef = this.firestore
                        .collection('staffUserChats')
                        .doc(participantId)
                        .collection('staffChats')
                        .doc(chatId).ref;

                    batch.set(
                        userChatRef,
                        {
                            lastMessage: lastMessageUpdate,
                            unreadCount: 0,
                            staffInfo: chat.staffInfo,
                        },
                        { merge: true }
                    );
                }

                await batch.commit();
            }

            // Notify listeners
            this._newMessageSubject.next({
                ...updatedMessages[messageIndex],
                chatId,
            });
        } catch (error) {
            console.error('Error deleting message:', error);
            throw error;
        }
    }

    /**
     * Updates message status and read receipts
     * @param chatId The chat ID
     * @param messageId The message ID
     * @param userId The user ID who read the message
     */
    async updateMessageStatus(
        chatId: string,
        messageId: string,
        userId: string
    ): Promise<void> {
        try {
            const chatDoc = await this.firestore
                .collection('staffChats')
                .doc(chatId)
                .get()
                .toPromise();
            if (!chatDoc?.exists) {
                throw new Error('Chat not found');
            }

            const chat = chatDoc.data() as Chat;
            const messageRef = this.firestore
                .collection('staffChats')
                .doc(chatId)
                .collection('messages')
                .doc(messageId);

            const messageDoc = await messageRef.get().toPromise();
            if (!messageDoc?.exists) {
                throw new Error('Message not found');
            }

            const message = messageDoc.data() as ChatMessage;
            const readBy = message.readBy || {};
            readBy[userId] = true;

            // Check if all participants have read the message
            const allParticipants = Object.keys(chat.participants);
            const allRead = allParticipants.every(
                (participantId) => readBy[participantId]
            );

            // Update message status
            await messageRef.update({
                readBy,
                status: allRead ? 'read' : 'delivered',
            });

            // If all participants have read, update the last message status in the chat
            if (allRead) {
                await this.firestore
                    .collection('staffChats')
                    .doc(chatId)
                    .update({
                        'lastMessage.status': 'read',
                    });
            }
        } catch (error) {
            console.error('Error updating message status:', error);
            throw error;
        }
    }

    /**
     * Marks all unread messages in a chat as read for a specific user
     * @param chatId The chat ID
     * @param userId The user ID who read the messages
     */
    async markMessagesAsRead(chatId: string, userId: string): Promise<void> {
        try {
            // Get the chat document
            const chatDoc = await this.firestore
                .collection('staffChats')
                .doc(chatId)
                .get()
                .toPromise();
            if (!chatDoc?.exists) {
                console.error('Chat not found:', chatId);
                return;
            }

            const chat = chatDoc.data() as Chat;
            if (!chat.messages || !Array.isArray(chat.messages)) {
                console.error('No messages found in chat:', chatId);
                return;
            }

            // Get current userChat document to get the current unread count
            const userChatDoc = await this.firestore
                .collection('staffUserChats')
                .doc(userId)
                .collection('staffChats')
                .doc(chatId)
                .get()
                .toPromise();

            const currentUnreadCount =
                userChatDoc?.data()?.['unreadCount'] || 0;

            // Filter messages that need to be marked as read
            const messagesToUpdate = chat.messages.filter(
                (message) =>
                    message.status == 'sent' || message.status == 'delivered'
            );

            if (messagesToUpdate.length === 0) {
                return;
            }

            // Create a batch operation
            const batch = this.firestore.firestore.batch();
            const chatRef = this.firestore
                .collection('staffChats')
                .doc(chatId).ref;

            // Update each message's readBy status
            const updatedMessages = chat.messages.map((message) => {
                if (message.status == 'sent' || message.status == 'delivered') {
                    const readBy = message.readBy || {};
                    readBy[userId] = true;

                    // Get all participants
                    const allParticipants = Object.keys(chat.participants);

                    // Check if chat is in private mode
                    if (chat.info?.isPrivate) {
                        // For private chat, only consider relevant participants
                        const relevantParticipants = allParticipants.filter(
                            (participantId) => {
                                // Always include staff members
                                if (participantId.startsWith('staff_')) {
                                    return true;
                                }

                                // For staff messages, include all staff and the target parent
                                if (message.senderId.startsWith('staff_')) {
                                    return (
                                        participantId.startsWith('staff_') ||
                                        participantId ===
                                            `parent_${message.targetGuardianId}`
                                    );
                                }

                                return false;
                            }
                        );

                        // Check if all relevant participants have read the message
                        const allRead = relevantParticipants.every(
                            (participantId) => readBy[participantId]
                        );
                        return {
                            ...message,
                            readBy,
                            status: allRead ? 'read' : 'delivered',
                        };
                    } else {
                        // For non-private chat, check all participants
                        const allRead = allParticipants.every(
                            (participantId) => readBy[participantId]
                        );
                        return {
                            ...message,
                            readBy,
                            status: allRead ? 'read' : 'delivered',
                        };
                    }
                }
                return message;
            });

            // Update the chat document with the new messages array
            batch.update(chatRef, {
                messages: updatedMessages,
                lastMessage: {
                    ...chat.lastMessage,
                    status: updatedMessages[updatedMessages.length - 1].status,
                },
            });

            // Update unread count in staffUserChats
            const userChatRef = this.firestore
                .collection('staffUserChats')
                .doc(userId)
                .collection('staffChats')
                .doc(chatId).ref;

            // Calculate unread count based on updated messages
            const unreadCount = updatedMessages.filter((message) => {
                // For private chat, only count relevant messages
                if (message.senderId.startsWith('staff_')) {
                    return (
                        (message.senderId === userId ||
                            `parent_${message.targetGuardianId}` === userId) &&
                        message.status !== 'read'
                    );
                }
                return false;
            }).length;

            // Update userChat with new unread count
            batch.update(userChatRef, {
                unreadCount: Math.max(
                    0,
                    currentUnreadCount - messagesToUpdate.length
                ),
                lastMessage: {
                    ...chat.lastMessage,
                    status: updatedMessages[updatedMessages.length - 1].status,
                },
            });

            // Commit the batch
            await batch.commit();
        } catch (error) {
            console.error('Error marking messages as read:', error);
            throw error;
        }
    }

    getUnreadCount(chatId: string, userId: string): Observable<number> {
        return this.getChatMessages(chatId).pipe(
            map((messages) => {
                return messages.filter((message) => {
                    if (message.senderId === userId) return false;
                    if (!message.readBy) return true;
                    return !message.readBy[userId];
                }).length;
            })
        );
    }

    getTotalUnreadCount(userId: string): Observable<number> {
        const currentUser = this.webStorage.getUserData();

        return this.firestore
            .collection('staffUserChats')
            .doc(userId)
            .collection('staffChats', (ref) => {
                let query = ref;

                // Add school filter if user is not admin
                query = query.where(
                    'schoolId',
                    '==',
                    currentUser.schoolId
                ) as any;

                return query;
            })
            .valueChanges()
            .pipe(
                map((chats) => {
                    return chats.reduce(
                        (total, chat) => total + (chat?.['unreadCount'] || 0),
                        0
                    );
                }),
                catchError((error) => {
                    console.error('Error getting total unread count:', error);
                    return of(0);
                })
            );
    }

    // Add the updateMessage helper method
    async updateMessage(
        chatId: string,
        messageId: string,
        updates: Partial<ChatMessage>
    ): Promise<void> // {
    //     return from(
    //         this.firestore
    //             .collection('staffChats')
    //             .doc(chatId)
    //             .collection('messages')
    //             .doc(messageId)
    //             .update(data)
    //     );
    // }
    {
        const chatRef = this.firestore.collection('staffChats').doc(chatId);
        const snap = await chatRef.get().toPromise();

        if (!snap?.exists) {
            throw new Error('Chat not found');
        }

        const chat = snap.data() as StaffChat;
        const messages = Array.isArray(chat.messages) ? [...chat.messages] : [];

        const idx = messages.findIndex((m) => m.id === messageId);
        if (idx === -1) {
            throw new Error('Message not found in messages array');
        }

        const now = Date.now();
        const original = messages[idx];

        // Merge updates and ensure edit flags
        // const edited = {
        //     ...original,
        //     ...updates,
        //     isEdited: true,
        //     editedAt: updates.editedAt ?? now,
        // };

        // Only set isEdited if the text content is being modified
        const isTextEdit = updates.text !== undefined && updates.text !== original.text;

        // Merge updates and conditionally set edit flags
        const edited = {
            ...original,
            ...updates,
            ...(isTextEdit && {
                isEdited: true,
                editedAt: updates.editedAt ?? now,
            }),
        };

        messages[idx] = edited;

        // Optional: keep lastMessage in sync if this is the last message
        const updateData: any = { messages };

        const isLast =
            messages.length > 0 &&
            messages[messages.length - 1]?.id === messageId;

        if (isLast) {
            // Adjust this block to match your lastMessage schema
            updateData.lastMessage = {
                ...(chat.lastMessage || {}),
                text: edited.text,
                senderId: edited.senderId ?? original.senderId,
                timestamp: edited.timestamp ?? original.timestamp,
                isDeleted: edited.isDeleted ?? original.isDeleted ?? false,
                status: edited.status ?? original.status ?? 'sent',
                id: edited.id ?? original.id, // include id if your schema uses it
            };
        }

        const batch = this.firestore.firestore.batch();
        batch.update(chatRef.ref, updateData);

        // If this was the last message, sync to staffUserChats for all participants
        if (isLast && updateData.lastMessage) {
            const participants = Object.keys(chat.participants || {});
            participants.forEach(participantId => {
                const userChatRef = this.firestore
                    .collection('staffUserChats')
                    .doc(participantId)
                    .collection('staffChats')
                    .doc(chatId).ref;
                
                batch.update(userChatRef, {
                    lastMessage: updateData.lastMessage
                });
            });
        }

        await batch.commit();
    }

    // Method to get chat by ID
    getChatById(chatId: string): Observable<ChatWithStaffUser | null> {
        return this.firestore
            .doc<StaffChat>(`staffChats/${chatId}`)
            .valueChanges()
            .pipe(
                switchMap((chat) => {
                    if (!chat) {
                        return of(null);
                    }

                    // Get the other user's ID (either staff or student)
                    const otherUserId = Object.keys(chat.participants).find(
                        (id) => id !== this.currentUserId
                    );
                    if (!otherUserId) {
                        return of(null);
                    }

                    // Get the other user's profile
                    return this.firestore
                        .doc<FirestoreUser>(`users/${otherUserId}`)
                        .valueChanges()
                        .pipe(
                            map((user) => {
                                if (!user) {
                                    return null;
                                }

                                return {
                                    type: 'staff',
                                    chatId: chat.chatId,
                                    lastMessage: {
                                        text: chat.lastMessage?.text || '',
                                        senderId:
                                            chat.lastMessage?.senderId || '',
                                        timestamp:
                                            chat.lastMessage?.timestamp ||
                                            Date.now(),
                                        isDeleted:
                                            chat.lastMessage?.isDeleted ||
                                            false,
                                    },
                                    unreadCount: 0,
                                    staffInfo: chat.staffInfo,
                                    otherUser: {
                                        id: user.id,
                                        name: user.profile.name,
                                        email: user.profile.email,
                                        role: user.profile.role,
                                        photoUrl: user.profile.photoURL,
                                        createdAt: Date.now(),
                                        status: user.profile.status,
                                    },
                                } as ChatWithStaffUser;
                            })
                        );
                })
            );
    }
    // searchMessagesAndStaffs(searchTerm: string, userId: string): Observable<Array<{ type: 'chat' | 'staff', data: any }>> {
    //   // console.log('🔍 Service: Starting search with term:', searchTerm, 'for user:', userId);

    //   if (!searchTerm.trim()) {
    //     // console.log('❌ Service: Empty search term, returning empty results');
    //     return of([]);
    //   }

    //   const searchTermLower = searchTerm.toLowerCase();

    //   // Extract only the numeric part from staff IDs
    //   const extractStaffNumber = (id: string | undefined): string | null => {
    //     if (!id) return null;
    //     const match = id.match(/(\d+)$/);
    //     return match ? match[1] : null;
    //   };

    //   // 🔹 Fetch chats where the user is a participant
    //   const chatSearch$ = this.firestore.collection<StaffChat>('staffChats', ref =>
    //     ref.where(`participants.${userId}`, '==', true)
    //   ).valueChanges().pipe(
    //     take(1),
    //     // tap(chats => console.log('📌 Service: Raw chats fetched:', chats.length)),
    //     map(chats => {
    //       return chats.filter(chat => {
    //         const hasMatchingMessage = chat.messages?.some((message: ChatMessage) =>
    //           message.text?.toLowerCase().includes(searchTermLower) && message.isDeleted == false
    //         );

    //         // Search by staff name
    //         const staffNameMatches = chat.staff2Info?.name?.toLowerCase().includes(searchTermLower);

    //         return hasMatchingMessage || staffNameMatches;
    //       }).map(chat => {
    //         // Extract just the numeric staff ID for comparison
    //         const staffNumber = extractStaffNumber(chat.staff2Info?.id);

    //         return {
    //           type: 'chat' as const,
    //           data: chat,
    //           staffId: staffNumber // Store just the numeric part
    //         };
    //       });
    //     }),
    //     // tap(filteredChats => console.log('✅ Service: Filtered chats:', filteredChats.length)),
    //     catchError(error => {
    //       console.error('❌ Service: Error fetching chats:', error);
    //       return of([]);
    //     })
    //   );

    //   // 🔹 Fetch staffs if the user is a staff member
    //   const staffSearch$ = userId.startsWith('staff_')
    //     ? this.lookupService.queryStaff({ 'name.contains': searchTerm }).pipe(
    //         map((response: HttpResponse<any[]>) => {
    //           const staffs = response.body || [];
    //           // console.log('📌 Service: Raw staffs fetched:', staffs.length);

    //           return staffs.map((staff: StaffDTO) => {
    //             // Extract just the numeric ID for comparison
    //             const staffNumber = extractStaffNumber(staff.id?.toString());
    //             const staffId = `staff_${staff.id}`;

    //             const roomIds = [staff.primaryRoomId];

    //             return {
    //               type: 'staff' as const,
    //               data: {
    //                 id: staffId,
    //                 profile: {
    //                   name: staff.name || '',
    //                   photoURL: staff.photoUuid || 'assets/layout/images/male.png'
    //                 },
    //                 otherUser: {
    //                   id: staffId,
    //                   name: staff.name,
    //                   email: staff.email || '',
    //                   role: 'staff',
    //                   photoUrl: staff.photoUuid || 'assets/layout/images/male.png',
    //                   createdAt: Date.now(),
    //                   status: 'offline',
    //                 },
    //                 roomIds: roomIds,
    //               },
    //               staffId: staffNumber // Store just the numeric part for comparison
    //             };
    //           });
    //         }),
    //         // tap(filteredStaffs => console.log('✅ Service: Filtered staffs:', filteredStaffs.length)),
    //         catchError(error => {
    //           console.error('❌ Service: Error fetching staffs:', error);
    //           return of([]);
    //         })
    //       )
    //     : of([]);

    //   // 🔹 Combine both searches and remove duplicates - following the student pattern exactly
    //   return forkJoin([chatSearch$, staffSearch$]).pipe(
    //     // tap(([chatResults, staffResults]) => {
    //     //   console.log('📌 Service: Raw chat results:', chatResults);
    //     //   console.log('📌 Service: Raw staff results:', staffResults);
    //     // }),
    //     map(([chatResults, staffResults]) => {
    //       const existingStaffIds = new Set(
    //         chatResults
    //           .filter(result => result.staffId) // Filter out null staffIds
    //           .map(result => result.staffId)
    //       );

    //       // console.log('📌 Service: Existing staff IDs for comparison:', Array.from(existingStaffIds));

    //       const filteredStaffResults = staffResults.filter(staff =>
    //         !existingStaffIds.has(staff.staffId)
    //       );

    //       const combinedResults = [
    //         ...chatResults.map(chat => ({
    //           type: 'chat' as const,
    //           data: chat.data
    //         })),
    //         ...filteredStaffResults.map(staff => ({
    //           type: 'staff' as const,
    //           data: staff.data
    //         }))
    //       ];

    //       // console.log('✅ Service: Final search results emitted:', combinedResults.length, combinedResults);
    //       return combinedResults;
    //     }),
    //     catchError(error => {
    //       console.error('❌ Service: Error in forkJoin:', error);
    //       return of([]);
    //     })
    //   );
    // }

    searchMessagesAndStaffs(
        searchTerm: string,
        userId: string
    ): Observable<Array<{ type: 'chat' | 'staff'; data: any }>> {
        // console.log('🔍 Service: Starting search with term:', searchTerm, 'for user:', userId);

        if (!searchTerm.trim()) {
            // console.log('❌ Service: Empty search term, returning empty results');
            return of([]);
        }

        const searchTermLower = searchTerm.toLowerCase();

        // Extract only the numeric part from staff IDs
        const extractStaffNumber = (id: string | undefined): string | null => {
            if (!id) return null;
            const match = id.match(/(\d+)$/);
            return match ? match[1] : null;
        };

        // 🔹 Fetch chats where the user is a participant
        const chatSearch$ = this.firestore
            .collection<StaffChat>('staffChats', (ref) =>
                ref.where(`participants.${userId}`, '==', true)
            )
            .valueChanges()
            .pipe(
                take(1),
                // tap(chats => console.log('📌 Service: Raw chats fetched:', chats.length)),
                switchMap((chats) => {
                    const filteredChats = chats.filter((chat) => {
                        // REMOVED: Search by message text - only searching by name now
                        // const hasMatchingMessage = chat.messages?.some(
                        //     (message: ChatMessage) =>
                        //         message.text
                        //             ?.toLowerCase()
                        //             .includes(searchTermLower) &&
                        //         message.isDeleted == false
                        // );

                        // Search by staff name - check BOTH participants for bidirectional search
                        const staff1NameMatches = chat.staffInfo?.name
                            ?.toLowerCase()
                            .includes(searchTermLower);
                        
                        const staff2NameMatches = chat.staff2Info?.name
                            ?.toLowerCase()
                            .includes(searchTermLower);

                        // Return true if EITHER staff member's name matches
                        // This ensures bidirectional search works correctly
                        return staff1NameMatches || staff2NameMatches;
                    });

                    if (filteredChats.length === 0) {
                        return of([]);
                    }

                    // Get profile images for each chat participant (similar to student implementation)
                    const chatWithProfileImages$ = filteredChats.map((chat) => {
                        // Determine which participant is the OTHER person (not the current user)
                        // This ensures bidirectional display works correctly
                        let participantId: string | undefined;
                        let participantStaffId: string | null = null;
                        
                        if (chat.staffInfo?.id === userId) {
                            // Current user is staffInfo, so show staff2Info
                            participantId = chat.staff2Info?.id;
                            participantStaffId = extractStaffNumber(chat.staff2Info?.id);
                        } else if (chat.staff2Info?.id === userId) {
                            // Current user is staff2Info, so show staffInfo
                            participantId = chat.staffInfo?.id;
                            participantStaffId = extractStaffNumber(chat.staffInfo?.id);
                        } else {
                            // Fallback: show staff2Info
                            participantId = chat.staff2Info?.id;
                            participantStaffId = extractStaffNumber(chat.staff2Info?.id);
                        }

                        if (!participantId) {
                            return of({
                                type: 'chat' as const,
                                data: {
                                    ...chat,
                                    participantProfileImage:
                                        'assets/layout/images/male.png',
                                },
                                staffId: participantStaffId,
                            });
                        }

                        // Fetch profile image using PhotoService
                        return this.firestore
                            .doc(`users/${participantId}`)
                            .valueChanges()
                            .pipe(
                                take(1),
                                switchMap((userProfile: any) => {
                                    const status = userProfile?.profile?.status;
                                    const photoUid = userProfile?.profile?.photoURL;
                                    
                                    return this.photoService.getPhotoUrl(photoUid).pipe(
                                        map(imageUrl => ({
                                            type: 'chat' as const,
                                            data: {
                                                ...chat,
                                                participantProfileImage: imageUrl,
                                                status : status
                                            },
                                            staffId: participantStaffId,
                                        }))
                                    );
                                }),
                                catchError(() =>
                                    of({
                                        type: 'chat' as const,
                                        data: {
                                            ...chat,
                                            participantProfileImage: 'assets/layout/images/male.png',
                                        },
                                        staffId: participantStaffId,
                                    })
                                )
                            );
                    });

                    return forkJoin(chatWithProfileImages$);
                }),
                // tap(filteredChats => console.log('✅ Service: Filtered chats with profile images:', filteredChats.length)),
                catchError((error) => {
                    console.error('❌ Service: Error fetching chats:', error);
                    return of([]);
                })
            );
            const currentUser = this.webStorage.getUserData();
        // 🔹 Fetch staffs if the user is a staff member
        const staffSearch$ = userId.startsWith('staff_')
            ? this.lookupService
                  .queryStaff({
                      'name.contains': searchTerm,
                      'designation.notEquals': 'Kiosk',
                      'schoolId.equals': currentUser.schoolId,
                      'profileStatus.notEquals': 'I',
                      'sort' : 'name,asc'    
                     })
                  .pipe(
                      map((response: HttpResponse<any[]>) => {
                          const staffs = response.body || [];
                          // console.log('📌 Service: Raw staffs fetched:', staffs.length);

                          return staffs.map((staff: StaffDTO) => {
                              // Extract just the numeric ID for comparison
                              const staffNumber = extractStaffNumber(
                                  staff.id?.toString()
                              );
                              const staffId = `staff_${staff.id}`;

                              const roomIds = [staff.primaryRoomId];

                              // Keep photoUuid for later processing (similar to student implementation)
                              const photoUuid = staff.photoUuid;

                              return {
                                  type: 'staff' as const,
                                  data: {
                                      id: staffId,
                                      profile: {
                                          name: staff.name || '',
                                          photoURL:
                                              photoUuid ||
                                              'assets/layout/images/male.png',
                                          photoUid: photoUuid, // Keep the original photoUid for later processing
                                      },
                                      otherUser: {
                                          id: staffId,
                                          name: staff.name,
                                          email: staff.email || '',
                                          role: 'staff',
                                          photoUrl:
                                              photoUuid ||
                                              'assets/layout/images/male.png',
                                          createdAt: Date.now(),
                                          status: staff.profileStatus,
                                      },
                                      roomIds: roomIds,
                                  },
                                  staffId: staffNumber, // Store just the numeric part for comparison
                              };
                          });
                      }),
                      // tap(filteredStaffs => console.log('✅ Service: Filtered staffs:', filteredStaffs.length)),
                      catchError((error) => {
                          return of([]);
                      })
                  )
            : of([]);

        // 🔹 Combine both searches and remove duplicates - following the student pattern exactly
        return forkJoin([chatSearch$, staffSearch$]).pipe(
            // tap(([chatResults, staffResults]) => {
            //   console.log('📌 Service: Raw chat results:', chatResults);
            //   console.log('📌 Service: Raw staff results:', staffResults);
            // }),
            map(([chatResults, staffResults]) => {
                const existingStaffIds = new Set(
                    chatResults
                        .filter((result) => result.staffId) // Filter out null staffIds
                        .map((result) => result.staffId)
                );

                // console.log('📌 Service: Existing staff IDs for comparison:', Array.from(existingStaffIds));

                const filteredStaffResults = staffResults.filter(
                    (staff) => !existingStaffIds.has(staff.staffId)
                );

                const combinedResults = [
                    ...chatResults.map((chat) => ({
                        type: 'chat' as const,
                        data: chat.data,
                    })),
                    ...filteredStaffResults.map((staff) => ({
                        type: 'staff' as const,
                        data: staff.data,
                    })),
                ];

                // console.log('✅ Service: Final search results emitted:', combinedResults.length, combinedResults);
                return combinedResults;
            }),
            catchError((error) => {
                console.error('❌ Service: Error in forkJoin:', error);
                return of([]);
            })
        );
    }


}
